// KanbanBoard — read-only stage-column board for the applicant pipeline
// Desktop: horizontal columns. Mobile (<md): collapsible vertical sections.

import { useState } from "react";

export interface KanbanCard {
  id: string;
  name: string;
  daysInPipeline: number;
  daysSinceActivity?: number;
  interest?: string;
  blockedBy?: string;
}

export interface KanbanColumn {
  stage: string;
  cards: KanbanCard[];
}

export interface KanbanBoardProps {
  columns: KanbanColumn[];
  isLoading?: boolean;
}

function daysBadgeClass(days: number): string {
  if (days > 30) return "bg-red-500/15 text-status-red";
  if (days >= 14) return "bg-amber-500/15 text-status-amber";
  return "bg-green-500/15 text-status-green";
}

function CardItem({ card }: { card: KanbanCard }) {
  return (
    <div className="glass rounded-xl p-3 flex flex-col gap-1.5 min-w-0">
      {/* Name */}
      <p className="text-sm font-semibold text-theme-primary leading-snug truncate">
        {card.name}
      </p>

      {/* Days in pipeline badge */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <span
          className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-xs font-semibold ${daysBadgeClass(card.daysInPipeline)}`}
        >
          {card.daysInPipeline}d in pipeline
        </span>

        {/* Interest pill */}
        {card.interest && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-xs bg-theme-overlay text-theme-muted truncate max-w-[100px]">
            {card.interest}
          </span>
        )}
      </div>

      {/* Days since activity */}
      {card.daysSinceActivity !== undefined && card.daysSinceActivity > 7 && (
        <p className="text-xs text-theme-muted leading-snug">
          Last activity {card.daysSinceActivity}d ago
        </p>
      )}

      {/* Blocked by */}
      {card.blockedBy && (
        <p className="text-xs font-semibold text-status-red leading-snug truncate">
          Blocked: {card.blockedBy}
        </p>
      )}
    </div>
  );
}

function ColumnSkeleton() {
  return (
    <div className="flex flex-col gap-2 w-48 shrink-0">
      <div className="skeleton h-6 w-36 rounded-md" />
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="skeleton h-20 rounded-xl" />
      ))}
    </div>
  );
}

/** Mobile accordion section — tap header to expand/collapse */
function MobileColumn({ col }: { col: KanbanColumn }) {
  const [open, setOpen] = useState(col.cards.length > 0);
  const hasUrgent = col.cards.some((c) => c.daysInPipeline > 30);
  const hasWarning =
    !hasUrgent && col.cards.some((c) => c.daysInPipeline >= 14);

  return (
    <div className="rounded-xl border border-theme-subtle overflow-hidden">
      {/* Collapsible header */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-theme-overlay hover:bg-theme-overlay/80 transition-colors min-h-[48px]"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 min-w-0">
          {hasUrgent && (
            <span className="w-2 h-2 rounded-full bg-status-red shrink-0" />
          )}
          {hasWarning && !hasUrgent && (
            <span className="w-2 h-2 rounded-full bg-status-amber shrink-0" />
          )}
          <h3 className="text-xs font-bold text-theme-primary uppercase tracking-wider truncate">
            {col.stage}
          </h3>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold bg-theme-raised text-theme-muted shrink-0">
            {col.cards.length}
          </span>
        </div>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`shrink-0 text-theme-muted transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* Cards list */}
      {open && (
        <div className="p-3 space-y-2 bg-theme-surface">
          {col.cards.length === 0 ? (
            <div className="rounded-lg border border-dashed border-theme-subtle flex items-center justify-center py-4">
              <p className="text-xs text-theme-muted">Empty</p>
            </div>
          ) : (
            col.cards.map((card) => <CardItem key={card.id} card={card} />)
          )}
        </div>
      )}
    </div>
  );
}

export default function KanbanBoard({
  columns,
  isLoading = false,
}: KanbanBoardProps) {
  if (isLoading) {
    return (
      <>
        {/* Desktop skeleton */}
        <div className="hidden md:block overflow-x-auto">
          <div
            className="flex gap-4 p-1 pb-3"
            style={{ minWidth: "max-content" }}
          >
            {Array.from({ length: 5 }).map((_, i) => (
              <ColumnSkeleton key={i} />
            ))}
          </div>
        </div>
        {/* Mobile skeleton */}
        <div className="md:hidden space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skeleton h-12 rounded-xl" />
          ))}
        </div>
      </>
    );
  }

  if (columns.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-2">
        <p className="text-sm text-theme-muted">
          No pipeline stages configured
        </p>
        <p className="text-xs text-theme-muted opacity-60">
          Applicant data will appear here after sync
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop: horizontal scrolling columns */}
      <div className="hidden md:block overflow-x-auto">
        <div
          className="flex gap-4 p-1 pb-3"
          style={{ minWidth: "max-content" }}
        >
          {columns.map((col) => (
            <div key={col.stage} className="flex flex-col gap-2 w-52 shrink-0">
              {/* Column header */}
              <div className="flex items-center justify-between gap-2 px-1">
                <h3 className="text-xs font-bold text-theme-primary uppercase tracking-wider truncate">
                  {col.stage}
                </h3>
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold bg-theme-overlay text-theme-muted shrink-0">
                  {col.cards.length}
                </span>
              </div>

              {/* Divider */}
              <div className="h-px bg-dcvfd-accent opacity-40 rounded-full" />

              {/* Cards */}
              <div className="flex flex-col gap-2">
                {col.cards.length === 0 ? (
                  <div className="rounded-xl border-2 border-dashed border-theme-subtle flex items-center justify-center h-16">
                    <p className="text-xs text-theme-muted">Empty</p>
                  </div>
                ) : (
                  col.cards.map((card) => (
                    <CardItem key={card.id} card={card} />
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile: collapsible vertical sections */}
      <div className="md:hidden space-y-2">
        {columns.map((col) => (
          <MobileColumn key={col.stage} col={col} />
        ))}
      </div>
    </>
  );
}
